// Wrapper para llamadas API

/**
 * Lee el token JWT de document.cookie.
 * La cookie auth_token no es HttpOnly (se establece via JS), así que es legible.
 * Usamos esto para enviarla como Authorization header y evitar el bloqueo
 * cross-origin de SameSite=Lax en Safari.
 */
function getAuthTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith('auth_token='));
  return match ? match.split('=').slice(1).join('=') : null;
}

/**
 * Normaliza una URL a HTTPS si el navegador está en una página HTTPS.
 * Evita errores de Mixed Content en producción (Cloud Run).
 * No afecta al desarrollo local (http://localhost queda como http://).
 */
function enforceHttpsIfNeeded(url: string): string {
  if (
    typeof window !== 'undefined' &&
    window.location.protocol === 'https:' &&
    url.startsWith('http://')
  ) {
    return url.replace('http://', 'https://');
  }
  return url;
}

// Helper para obtener la URL base de la API
// En producción fuerza HTTPS para evitar Mixed Content
function getApiBaseUrl(): string {
  const envUrl = process.env.NEXT_PUBLIC_API_URL;

  // Si hay URL configurada via variable de entorno, usarla
  // pero forzar HTTPS si el navegador está en HTTPS (evita Mixed Content cuando
  // NEXT_PUBLIC_API_URL tiene http:// en Cloud Run)
  if (envUrl) {
    return enforceHttpsIfNeeded(envUrl);
  }

  // En el navegador, detectar si estamos en producción (Cloud Run)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Si estamos en Cloud Run (producción), derivar la URL del backend desde el hostname del frontend
    if (hostname.includes('.run.app')) {
      const backendUrl = hostname.replace('mipymex-caabsa-frontend', 'mipymex-caabsa-backend');
      return `https://${backendUrl}/api/v1`;
    }
  }

  // Fallback para desarrollo local
  return "http://localhost:8000/api/v1";
}

const API_BASE_URL = getApiBaseUrl();

// Exportar para uso en otros servicios
export { getApiBaseUrl, API_BASE_URL };

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  status: number;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  try {
    // Si el body es FormData, no establecer Content-Type (el navegador lo hace automáticamente)
    const isFormData = options.body instanceof FormData;

    // Incluir el token como Authorization header para compatibilidad con Safari.
    // Safari bloquea cookies SameSite=Lax en peticiones cross-origin (puerto distinto),
    // por lo que enviamos el token explícitamente en el header en lugar de confiar en la cookie.
    const token = getAuthTokenFromCookie();
    const authHeader: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    const headers: HeadersInit = isFormData
      ? { ...authHeader, ...(options.headers as HeadersInit) }
      : {
          "Content-Type": "application/json",
          ...authHeader,
          ...(options.headers as HeadersInit),
        };

    const isDev = process.env.NODE_ENV === "development";
    if (isDev) {
      console.log(`[API] ${options.method || "GET"} ${endpoint}`);
    }

    // Llamar getApiBaseUrl() por request (no la constante) para asegurar HTTPS
    // incluso si el módulo se inicializó en SSR con window undefined.
    const requestUrl = `${getApiBaseUrl()}${endpoint}`;

    const response = await fetch(requestUrl, {
      ...options,
      headers,
      credentials: "include", // Envía cookies (auth) en peticiones cross-origin; backend debe devolver CORS con Allow-Credentials
    });

    if (isDev) console.log(`[API] ${response.status} ${requestUrl}`);

    // Si la respuesta no es JSON, manejar el error
    let data;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      const text = await response.text();
      if (isDev) console.error(`[API] Non-JSON response from ${endpoint}`);
      return {
        error: text || `Error ${response.status}: ${response.statusText}`,
        status: response.status,
      };
    }

    // Extraer mensaje de error, manejando arrays de validación de Pydantic
    let errorMessage: string | undefined;
    if (!response.ok) {
      if (Array.isArray(data.detail)) {
        // Pydantic validation errors: [{type, loc, msg, input}, ...]
        errorMessage = data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
      } else if (typeof data.detail === 'object' && data.detail !== null) {
        errorMessage = data.detail.msg || data.detail.message || JSON.stringify(data.detail);
      } else {
        errorMessage = data.detail || data.error || data.message || `Error ${response.status}`;
      }
    }

    return {
      data: response.ok ? data : undefined,
      error: errorMessage,
      status: response.status,
    };
  } catch (error) {
    const msg = error instanceof Error ? error.message : "Error de conexión";
    if (process.env.NODE_ENV === "development") console.error("[API Exception]", error);
    // NetworkError / Failed to fetch suelen indicar CORS, backend caído o URL incorrecta
    const friendly =
      typeof msg === "string" && (msg.includes("fetch") || msg.includes("NetworkError"))
        ? "No se pudo conectar con el servidor. Comprueba que la API esté en ejecución y que CORS esté configurado."
        : msg;
    return {
      error: friendly,
      status: 500,
    };
  }
}

export const api = {
  get: <T>(endpoint: string, options: RequestInit = {}) => 
    apiRequest<T>(endpoint, { ...options, method: "GET" }),
  post: <T>(endpoint: string, body: any, options: RequestInit = {}) => {
    // Si body es FormData, no stringificarlo
    const isFormData = body instanceof FormData;
    // apiRequest ya inyecta el Authorization header, pasar headers mínimos
    return apiRequest<T>(endpoint, {
      ...options,
      method: "POST",
      body: isFormData ? body : JSON.stringify(body),
      headers: isFormData
        ? { ...(options.headers as HeadersInit) }
        : { "Content-Type": "application/json", ...(options.headers as HeadersInit) },
    });
  },
  put: <T>(endpoint: string, body: any, options: RequestInit = {}) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: "PUT",
      body: JSON.stringify(body),
    }),
  delete: <T>(endpoint: string, options: RequestInit = {}) => 
    apiRequest<T>(endpoint, { ...options, method: "DELETE" }),
};
